import { useState } from "react";
import { ShieldAlert, KeyRound, AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import logo from "@/assets/logo.png";

export const SafetyWarningModal = () => {
  const { user, loading, setUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  if (loading || !user || user.safety_ack) return null;

  const dismiss = async () => {
    if (!checked || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/profile/safety-ack");
      setUser(data);
    } catch {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="safety-warning-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
    >
      <div
        data-testid="safety-warning-modal"
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-red-500/40 bg-[#0b0b10] shadow-[0_0_60px_rgba(239,68,68,0.35)]"
      >
        <div className="flex items-center justify-center gap-3 border-b border-white/10 bg-black/60 px-6 py-4">
          <img
            src={logo}
            alt="Autono Dex"
            className="h-9 w-9 drop-shadow-[0_0_12px_rgba(0,240,255,0.5)]"
          />
          <span className="font-heading text-lg font-extrabold uppercase tracking-widest text-white">
            Autono<span className="text-gradient-accent"> Dex</span>
          </span>
        </div>

        <div className="flex items-center gap-3 border-b border-red-500/30 bg-red-500/10 px-6 py-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-500/20 ring-1 ring-red-400/50">
            <ShieldAlert className="h-6 w-6 text-red-400" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold uppercase tracking-wide text-red-300">
              Security Warning
            </h2>
            <p className="text-xs font-mono uppercase tracking-wider text-red-400/70">
              Read before continuing
            </p>
          </div>
        </div>

        <div className="space-y-4 px-6 py-6">
          <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <p className="text-sm font-semibold leading-relaxed text-slate-100">
              SCAMMERS are everywhere. It is super important to never share your wallet private keys.
            </p>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-cyan-400" />
            <p className="text-sm leading-relaxed text-slate-300">
              No one and no company will ever need those keys. Anyone asking for your private keys or
              seed phrase is trying to steal your funds.
            </p>
          </div>
          <p className="text-xs leading-relaxed text-slate-500">
            Autonomous Dex is not responsible for any information you freely give to other members.
          </p>

          <label
            data-testid="safety-warning-checkbox-label"
            className="flex cursor-pointer select-none items-start gap-3 rounded-xl border border-white/10 bg-black/40 p-4 transition-colors hover:border-cyan-400/40"
          >
            <input
              type="checkbox"
              data-testid="safety-warning-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-cyan-400"
            />
            <span className="text-sm text-slate-200">
              I understand and will never share my wallet private keys with anyone.
            </span>
          </label>
        </div>

        <div className="border-t border-white/10 px-6 py-4">
          <button
            data-testid="safety-warning-dismiss"
            onClick={dismiss}
            disabled={!checked || saving}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-600 px-4 py-3 font-heading font-bold text-black transition-all enabled:hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Saving..." : "I Understand — Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};
